import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Navigate } from 'react-router-dom';
import apiService from '../services/api-service';
import NavBar from './nav-bar';
import styles from './learning-page.module.css';

export class LearningPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      // Question state
      question: null,
      questionId: null,
      questionFeatures: null,
      reviewHistory: null,
      answer: '',
      isSubmitting: false,

      // Timing
      questionStartTime: null,
      responseTime: null,

      // Feedback
      feedback: null,
      showFeedback: false,
      correct: null,

      // Stats
      stats: {
        totalReviews: 0,
        correctAnswers: 0,
        currentStreak: 0
      },

      // State
      loading: true,
      error: null
    };
  }

  async componentDidMount() {
    this.loadNextQuestion();
  }

  async loadNextQuestion() {
    this.setState({ loading: true, error: null });

    try {
      const data = await apiService.getNextQuestion();

      if (!data.question) {
        this.setState({
          question: null,
          loading: false,
          error: 'No questions available. Great job!'
        });
        return;
      }

      this.setState({
        question: data.question,
        questionId: data.questionId,
        questionFeatures: data.questionFeatures,
        reviewHistory: data.reviewHistory,
        stats: data.stats,
        questionStartTime: Date.now(),
        answer: '',
        showFeedback: false,
        feedback: null,
        correct: null,
        loading: false
      });

    } catch (error) {
      this.setState({
        error: error.message,
        loading: false
      });
    }
  }

  handleAnswerChange = (e) => {
    this.setState({ answer: e.target.value });
  }

  handleSubmit = async (e) => {
    e.preventDefault();

    if (!this.state.answer.trim()) {
      return;
    }

    this.setState({ isSubmitting: true });

    const responseTime = Date.now() - this.state.questionStartTime;

    try {
      // The server grades the answer and predicts the next interval itself
      // (v2 model on OVMS / Intel iGPU, local tfjs fallback). The browser-side
      // WebGPU model is now only used by the /ml-demo showcase page.
      const result = await apiService.submitAnswer(this.state.answer, responseTime);

      this.setState({
        correct: result.correct,
        feedback: result.feedback,
        showFeedback: true,
        isSubmitting: false,
        stats: result.stats
      });

    } catch (error) {
      this.setState({
        error: error.message,
        isSubmitting: false
      });
    }
  }

  handleNext = () => {
    this.loadNextQuestion();
  }

  render() {
    if (!this.props.loggedIn) {
      return <Navigate to="/" replace />;
    }

    const { question, answer, showFeedback, feedback, correct, stats, loading, error, isSubmitting } = this.state;

    return (
      <div className={styles.learningPage}>
        <div className={styles.learningContainer}>
          <h1>🧠 Neural-Enhanced Learning</h1>

          {/* Navigation */}
          <NavBar />

          {/* Stats Bar */}
          <div className={styles.statsBar}>
            <div className={styles.stat}>
              <label>Reviews</label>
              <span className={styles.value}>{stats.totalReviews}</span>
            </div>
            <div className={styles.stat}>
              <label>Correct</label>
              <span className={`${styles.value} ${styles.correct}`}>{stats.correctAnswers}</span>
            </div>
            <div className={styles.stat}>
              <label>Streak</label>
              <span className={`${styles.value} ${styles.streak}`}>{stats.currentStreak}🔥</span>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className={styles.errorMessage}>
              {error}
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className={styles.loading}>
              <div className={styles.spinner}></div>
              <p>Loading question...</p>
            </div>
          )}

          {/* Question Display */}
          {!loading && question && !showFeedback && (
            <div className={styles.questionCard}>
              <div className={styles.questionLabel}>Translate to English:</div>
              <div className={styles.questionText}>{question}</div>

              <form onSubmit={this.handleSubmit} className={styles.answerForm}>
                <input
                  type="text"
                  value={answer}
                  onChange={this.handleAnswerChange}
                  placeholder="Type your answer..."
                  className={styles.answerInput}
                  autoFocus
                  disabled={isSubmitting}
                />

                <button
                  type="submit"
                  className={styles.submitButton}
                  disabled={isSubmitting || !answer.trim()}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Answer'}
                </button>
              </form>
            </div>
          )}

          {/* Feedback Display */}
          {showFeedback && feedback && (
            <div className={`${styles.feedbackCard} ${correct ? styles.correct : styles.incorrect}`}>
              <div className={styles.feedbackHeader}>
                <span className={styles.feedbackIcon}>
                  {correct ? '✓' : '✗'}
                </span>
                <span className={styles.feedbackText}>
                  {correct ? 'Correct!' : 'Not Quite'}
                </span>
              </div>

              {!correct && (
                <div className={styles.correctAnswer}>
                  The answer was: <strong>{feedback.correctAnswer || 'N/A'}</strong>
                </div>
              )}

              <div className={styles.intervalInfo}>
                <h3>Next Review</h3>
                <div className={styles.intervalDisplay}>
                  <div className={styles.intervalValue}>{feedback.intervalUsed}</div>
                  <div className={styles.intervalUnit}>days</div>
                </div>
                <div className={styles.intervalDate}>
                  {new Date(feedback.nextReviewDate).toLocaleDateString()}
                </div>
              </div>

              {/* Algorithm Comparison */}
              <div className={styles.algorithmComparison}>
                <h4>Algorithm Predictions</h4>
                <div className={styles.comparisonGrid}>
                  <div className={`${styles.algorithmPred} ${styles.baseline}`}>
                    <label>SM-2 Baseline</label>
                    <span className={styles.value}>{feedback.baselineInterval} days</span>
                  </div>

                  {feedback.mlInterval !== null && (
                    <div className={`${styles.algorithmPred} ${styles.ml}`}>
                      <label>ML Enhanced</label>
                      <span className={styles.value}>{feedback.mlInterval} days</span>
                    </div>
                  )}

                  <div className={styles.algorithmUsed}>
                    <label>Used</label>
                    <span className={`${styles.value} ${styles[feedback.algorithmUsed]}`}>
                      {feedback.algorithmUsed.toUpperCase()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Performance Stats */}
              <div className={styles.performanceStats}>
                <div className={styles.perfStat}>
                  <label>Quality</label>
                  <span className={styles.value}>{feedback.quality}/5</span>
                </div>
                <div className={styles.perfStat}>
                  <label>Success Rate</label>
                  <span className={styles.value}>{(feedback.stats.successRate * 100).toFixed(0)}%</span>
                </div>
                <div className={styles.perfStat}>
                  <label>Total Reviews</label>
                  <span className={styles.value}>{feedback.stats.totalReviews}</span>
                </div>
                <div className={styles.perfStat}>
                  <label>Consecutive</label>
                  <span className={styles.value}>{feedback.stats.consecutiveCorrect}</span>
                </div>
              </div>

              <button
                onClick={this.handleNext}
                className={styles.nextButton}
              >
                Next Question →
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  loggedIn: state.auth.currentUser !== null
});

export default connect(mapStateToProps)(LearningPage);
